import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/kfmApi";
import { useAuth } from "@/context/AuthContext";

const BookmarksContext = createContext(null);

export const keyOf = (item = {}) =>
  item.kind === "word"
    ? `w:${item.strongs_number || item.word || ""}`
    : `v:${item.reference || ""}|${(item.translation || "").toLowerCase()}`;

export function BookmarksProvider({ children }) {
  const { user } = useAuth();
  const [bookmarks, setBookmarks] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [busyKey, setBusyKey] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/bookmarks");
      setBookmarks(data || []);
    } catch (e) {
      /* not signed in / offline — ignore */
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (user) refresh();
    else {
      setBookmarks([]);
      setLoaded(false);
    }
  }, [user, refresh]);

  const findId = (payload) => {
    const k = keyOf(payload);
    const hit = bookmarks.find((b) => keyOf(b) === k);
    return hit ? hit.id : null;
  };
  const isSaved = (payload) => !!findId(payload);

  const toggle = async (payload) => {
    const k = keyOf(payload);
    setBusyKey(k);
    try {
      const id = findId(payload);
      if (id) {
        await api.delete(`/bookmarks/${id}`);
        setBookmarks((prev) => prev.filter((b) => b.id !== id));
        return { saved: false };
      }
      const { data } = await api.post("/bookmarks", payload);
      setBookmarks((prev) => [data, ...prev.filter((b) => b.id !== data.id)]);
      return { saved: true };
    } finally {
      setBusyKey("");
    }
  };

  const remove = async (id) => {
    setBookmarks((prev) => prev.filter((b) => b.id !== id));
    try {
      await api.delete(`/bookmarks/${id}`);
    } catch (e) {
      refresh();
    }
  };

  return (
    <BookmarksContext.Provider
      value={{ bookmarks, loaded, isSaved, toggle, remove, refresh, busyKey, keyOf }}
    >
      {children}
    </BookmarksContext.Provider>
  );
}

export function useBookmarks() {
  const ctx = useContext(BookmarksContext);
  if (!ctx) {
    // Safe no-op fallback if used outside the provider (e.g. public views).
    return {
      bookmarks: [],
      loaded: false,
      isSaved: () => false,
      toggle: async () => ({ saved: false }),
      remove: async () => {},
      refresh: async () => {},
      busyKey: "",
      keyOf,
    };
  }
  return ctx;
}
